import update from 'immutability-helper';
import React from 'react';
import { connect } from 'react-redux';
import { ITranslateProps, translate } from '../../../mixins/translate';
import * as companyActions from '../../../store/company/actions';
import { fetchGet, fetchJson } from '../../../store/fetch';
import { AssetTypes as assetTypes } from '../../../utils/enums/AssetTypes';
import { IConnectProps } from '../../../utils/IConnectProps';
import { PanelBody, PanelHeader } from '../../elements';
import { DraggableContainer } from '../../react-smooth-dnd/DraggableContainer';
import { DraggableItem } from '../../react-smooth-dnd/DraggableItem';
import { ComponentSetStatePromised } from '../../utils/ComponentSetStatePromised';
import { DisplayMessage as DisplayError } from '../../utils/displayError';
import { DisplayMessage } from '../../utils/displayMessage';
import { showAssetInNewWindow } from '../../utils/showAssetInNewWindow';
import { AssetDropzone } from '../Common/AssetDropzone';
import { AssetTemplate } from '../Common/AssetTemplate';
import { getAssetOrdersAfterReorder } from '../Common/getAssetOrdersAfterReorder';
import { IAsset } from '../Interfaces/IAsset';

interface ISurveyEditVisualsProps extends ITranslateProps, IConnectProps {
    surveyId: string;
    objectId: string;
    elementGroupName?: undefined | string | null;
    elementId?: undefined | string;
    unitId?: undefined | string;
    manifestationId?: undefined | string;
    failureModeId?: string | undefined;
    selectedName?: undefined | string;
    selectedFailureModeUpdate: (selectedFailureModeId: string) => void;
    objectTreeFilterOnElementGroups: boolean;
}

interface ISurveyEditVisualsState {
    message?: undefined | string;
    submitting: boolean;
    failed: boolean;
    saved: boolean;
    errorCode?: undefined | string;

    storageUsed?: undefined | number;

    objectName?: undefined | string;
    objectCompositionIsVisible?: undefined | boolean;

    assets: IAsset[];
    isUploading?: undefined | boolean;
}

function mapStateToProps(state: any) {
    const { language } = state;
    return {
        languageCode: language.code,
    };
}

class SurveyEditVisuals extends ComponentSetStatePromised<ISurveyEditVisualsProps, ISurveyEditVisualsState> {
    constructor(props: ISurveyEditVisualsProps) {
        super(props);

        this.state = {
            submitting: false,
            failed: false,
            saved: false,
            assets: [],
        };
    }

    public override async componentDidMount() {
        await this.loadAssets(this.props.elementId, this.props.unitId, this.props.manifestationId);
    }

    public override async componentDidUpdate(prevProps: ISurveyEditVisualsProps) {
        if (prevProps.elementId !== this.props.elementId) {
            await this.loadAssets(this.props.elementId, this.props.unitId, this.props.manifestationId);
        }
        if (prevProps.unitId !== this.props.unitId) {
            await this.loadAssets(this.props.elementId, this.props.unitId, this.props.manifestationId);
        }
        if (prevProps.manifestationId !== this.props.manifestationId) {
            await this.loadAssets(this.props.elementId, this.props.unitId, this.props.manifestationId);
        }
    }

    public override render() {
        return (
            <>
                <PanelBody isForm={true}>
                    {this.renderMessage()}
                    {this.renderError()}
                    <PanelHeader text={this.props.t('common.visuals')} />

                    <div className="pr20 pl20">
                        <AssetDropzone
                            acceptedFiles=".png,.jpeg,.jpg,.xls,.xlsx,.pdf"
                            onComplete={this.onDropzoneComplete.bind(this)}
                            onError={this.onDropzoneError.bind(this)}
                            objectId={this.props.objectId}
                            surveyId={this.props.surveyId}
                            // elementId={this.props.elementId}
                            manifestationId={this.props.manifestationId}
                            autoProcessQueue={true}
                            unitId={this.props.manifestationId ? undefined : this.props.unitId}
                            onSending={() => this.setState({ isUploading: true })}
                            onQueueComplete={() => this.setState({ isUploading: false })}
                        />
                    </div>

                    {this.state.assets.length > 0 && (
                        <div className="section row pt10 pr20 pl20 bg-light dark">
                            <DraggableContainer
                                onDrop={async (result: any) => this.handleAssetsSorted(result)}
                                orientation="horizontal"
                                dragHandleSelector=".column-drag-handle"
                            >
                                {this.state.assets.map((asset, i) => (
                                    <DraggableItem key={asset.id} className="col-sm-6 col-md-3">
                                        <AssetTemplate
                                            key={asset.id}
                                            {...asset}
                                            order={i}
                                            objectId={this.props.objectId}
                                            surveyId={this.props.surveyId}
                                            onShowAsset={this.onShowAsset.bind(this)}
                                            onAssetTextChange={this.handleAssetTextChange.bind(this)}
                                            onAssetsChanged={this.handleRemovedAsset.bind(this)}
                                            onAssetsSorted={this.handleAssetsSorted.bind(this)}
                                            submitting={this.state.submitting}
                                            isEemuaOrApi={false}
                                            canRemove={true}
                                            canAddAnnotations={true}
                                            returnUrl={this.getReturnUrl()}
                                            canAddToReport={true}
                                            canAddToReportDocument={true}
                                            canEditDescription={true}
                                            canCopyAndMove={false}
                                            canSort={true}
                                            canAddToStartPage
                                        />
                                    </DraggableItem>
                                ))}
                            </DraggableContainer>
                        </div>
                    )}

                    {this.state.assets.length !== 0 && (
                        <div className="text-right">
                            <a
                                href="#"
                                className="btn btn-primary btn-sm fw600 ml10"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (window.confirm(this.props.t('common.confirmMultiDelete'))) {
                                        void this.removeAllAssets(e);
                                    }
                                }}
                            >
                                <span className="fa fa-trash-o pr5"></span> {this.props.t('survey.removeAllFiles')}
                            </a>
                        </div>
                    )}
                </PanelBody>
            </>
        );
    }

    private getReturnUrl() {
        let result = `/surveys/${this.props.surveyId}/verification?openCraConditionVerification=true`;

        if (this.props.elementId) {
            result += `&elementId=${this.props.elementId}`;
        }
        if (this.props.unitId) {
            result += `&unitId=${this.props.unitId}`;
        }
        if (this.props.manifestationId) {
            result += `&manifestationId=${this.props.manifestationId}`;
        }
        if (this.props.objectTreeFilterOnElementGroups) {
            result += '&filterOnElementGroups=true';
        }
        if (this.props.elementGroupName) {
            result += `&elementGroupName=${this.props.elementGroupName}`;
        }
        return result;
    }

    private async handleAssetsSorted(sortResult: { removedIndex: number; addedIndex: number }) {
        const assetOrders = getAssetOrdersAfterReorder(this.state.assets, sortResult);

        await this.setStatePromised({ submitting: true, failed: false, errorCode: undefined });
        const url = `/api/assets/order?surveyId=${this.props.surveyId}`;
        const result = await fetchJson(url, 'POST', { assetOrders });
        if (result.successful) {
            await this.setStatePromised({ submitting: false });
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private onShowAsset(e: React.MouseEvent<any>, asset: IAsset) {
        e.preventDefault();
        showAssetInNewWindow(asset);
    }

    private handleAssetTextChange(description: string, assetId: string) {
        const elementPos = this.state.assets.map((item: any) => item.id).indexOf(assetId);

        this.setState((state) => ({
            assets: update(state.assets, { [elementPos]: { description: { $set: description } } }),
        }));
    }

    private onDropzoneError(errorCode: string) {
        this.setState({ submitting: false, errorCode, failed: true });
    }

    private async onDropzoneComplete() {
        await this.loadAssets(this.props.elementId, this.props.unitId, this.props.manifestationId);
        this.props.dispatch(companyActions.getCompany());
        await this.loadSize();
        // this.props.onItemChanged();
        this.props.selectedFailureModeUpdate('');
    }

    private async removeAllAssets(e: any) {
        e.preventDefault();

        let url = `/api/removeAllAssets/?surveyId=${this.props.surveyId}`;
        if (this.props.elementId) {
            url += `&elementId=${this.props.elementId}`;
        }
        if (this.props.unitId) {
            url += `&unitId=${this.props.unitId}`;
        }
        if (this.props.manifestationId) {
            url += `&manifestationId=${this.props.manifestationId}`;
        }
        await this.setStatePromised({ submitting: true });
        const result = await fetchJson(url, 'DELETE');
        if (result.successful) {
            await this.loadAssets(this.props.elementId, this.props.unitId, this.props.manifestationId);
            await this.props.dispatch(companyActions.getCompany());
            await this.loadSize(); // display total size for survey
            await this.setStatePromised({ submitting: false });
            // this.props.onItemChanged();
            this.props.selectedFailureModeUpdate('');
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private async handleRemovedAsset() {
        // const url = `/api/surveys/${this.props.surveyId}/assets/${id}`;
        await this.setStatePromised({ submitting: true });
        // await fetchJsonWithThrow(url, 'DELETE');
        await this.loadAssets(this.props.elementId, this.props.unitId, this.props.manifestationId);
        this.props.dispatch(companyActions.getCompany());
        await this.loadSize(); // display total size for survey
        await this.setStatePromised({ submitting: false });
        // this.props.onItemChanged();
        this.props.selectedFailureModeUpdate('');
    }

    private async loadSize() {
        const url = `/api/surveysize/${this.props.surveyId}`;
        await this.setStatePromised({ submitting: true });
        const result = await fetchJson(url, 'GET');
        if (result.successful) {
            await this.setStatePromised({ storageUsed: result.json.storageUsed, submitting: false });
        } else {
            await this.setStatePromised({ submitting: false, failed: true, errorCode: result.errorCode });
        }
    }

    private async loadAssets(elementId?: string, unitId?: string, manifestationId?: string) {
        let url = `/api/assets?surveyId=${this.props.surveyId}&types=${assetTypes.Video},${assetTypes.Image},${assetTypes.Image360},${assetTypes.DerivedVideo},${assetTypes.DerivedImage},${assetTypes.MeasurementXLS},${assetTypes.MeasurementPDF},${assetTypes.ImageAnnotation}`;
        if (manifestationId) {
            url += `&manifestationId=${manifestationId}`;
        } else if (unitId) {
            url += `&unitId=${unitId}`;
        } else if (elementId) {
            url += `&elementId=${elementId}`;
        }
        if (!elementId && !unitId && !manifestationId) {
            url += '&unassigned=true';
        }

        await this.setStatePromised({ submitting: true });
        const result = await fetchGet(url, {}, ['404']);
        await this.setStatePromised({ submitting: false, assets: result.json });
    }

    private renderError(): JSX.Element | null {
        if (!this.state.failed) {
            return null;
        }
        let message: string;
        switch (this.state.errorCode) {
            case 'STORAGE_LIMIT_REACHED':
                message = this.props.t('asset.errorStorageLimitReached');
                break;
            case 'FILE_TOO_BIG':
                message = this.props.t('asset.errorFileTooBig');
                break;
            default:
                message = this.props.t('validation.general');
        }

        return <DisplayError message={message} />;
    }

    private renderMessage(): JSX.Element | null {
        if (this.state && this.state.message) {
            return <DisplayMessage message={this.state.message} />;
        }
        return null;
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps as any, undefined, undefined, { forwardRef: true })(SurveyEditVisuals));
